import { CellChange, DefaultCellTypes, ReactGrid } from '@silevis/reactgrid';
import '@silevis/reactgrid/styles.css';
import React, { useCallback, useContext, useMemo, useState } from 'react';
import { LcaResultModel } from 'types/types';
import { applyChanges, CellErrorMap, getDataRows, getGridColumns, getHeaderRow } from './gridMapping';
import { validateCellValue } from './gridValidation';
import { MODULE_FIELDS } from './gridMapping';
import { ReactGridEditorGridWrapper } from './ReactGridEditor.styled';
import LcaGridErrorsContext from '../grids/lca-results/util/LcaGridErrorsContext';

type Props = {
  rows: LcaResultModel[];
  onRowsChange: (updatedRows: LcaResultModel[]) => void;
};

/**
 * Converts LcaGridErrorsContext errors (keyed by `data[rowIndex]`) into CellErrorMap
 * (keyed by `${rowId}:${field}`).
 */
function mapContextErrors(
  contextErrors: Record<string, Record<string, string>> | null | false,
  rows: LcaResultModel[]
): CellErrorMap {
  if (!contextErrors) return {};

  const mapped: CellErrorMap = {};
  rows.forEach((row, index) => {
    const errorKey = `data[${index}]`;
    const rowErrors = contextErrors[errorKey];
    if (!rowErrors) return;

    const rowId = row.indicatorId || `row-${index}`;
    for (const field of MODULE_FIELDS) {
      const fieldError = rowErrors[field as keyof typeof rowErrors];
      if (fieldError) {
        mapped[`${rowId}:${field}`] = typeof fieldError === 'string' ? fieldError : 'Validation error';
      }
    }
  });

  return mapped;
}

const ReactGridEditor: React.FC<Props> = ({ rows, onRowsChange }) => {
  const columns = useMemo(() => getGridColumns(), []);
  const headerRow = useMemo(() => getHeaderRow(), []);
  const [cellErrors, setCellErrors] = useState<CellErrorMap>({});
  const contextErrors = useContext(LcaGridErrorsContext);

  const externalErrors = useMemo(() => mapContextErrors(contextErrors, rows), [contextErrors, rows]);

  const mergedErrors = useMemo(() => ({ ...externalErrors, ...cellErrors }), [externalErrors, cellErrors]);

  const dataRows = useMemo(() => getDataRows(rows, mergedErrors), [rows, mergedErrors]);
  const allRows = useMemo(() => [headerRow, ...dataRows], [headerRow, dataRows]);

  const handleChanges = useCallback(
    (changes: CellChange<DefaultCellTypes>[]) => {
      const newErrors: CellErrorMap = { ...cellErrors };
      let hasValidChanges = false;

      const validChanges = changes.filter((change) => {
        const fieldName = change.columnId as keyof LcaResultModel;
        if (!MODULE_FIELDS.includes(fieldName)) return false;

        const newCell = change.newCell as { text: string };
        const errorKey = `${change.rowId}:${change.columnId}`;
        const error = validateCellValue(newCell.text, fieldName);

        if (error) {
          newErrors[errorKey] = error;
          return false;
        }

        delete newErrors[errorKey];
        hasValidChanges = true;
        return true;
      });

      setCellErrors(newErrors);

      if (!hasValidChanges) return;

      const updatedRows = applyChanges(validChanges, rows);
      onRowsChange(updatedRows);
    },
    [rows, onRowsChange, cellErrors]
  );

  return (
    <ReactGridEditorGridWrapper>
      <ReactGrid rows={allRows} columns={columns} onCellsChanged={handleChanges} enableFillHandle enableRangeSelection />
    </ReactGridEditorGridWrapper>
  );
};

export default ReactGridEditor;
